import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { FaFileExcel } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './TestList.module.css';
import AddTestForm from '../component/AddTestForm';
import BulkUploadModal from '../component/BulkUploadModal';
import { ROLES } from '../config/roles';

const API = process.env.REACT_APP_API_BASE_URL;
const normalizeName = v => String(v ?? '').trim().toLowerCase();

const TestList = () => {
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [testQuery, setTestQuery] = useState('');
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [aimsCharges, setAimsCharges] = useState([]);
    const [priceListEntries, setPriceListEntries] = useState([]);
    const [coverageLoading, setCoverageLoading] = useState(false);
    const [showCoverageModal, setShowCoverageModal] = useState(false);
    const [coverageFilter, setCoverageFilter] = useState('missingAny');
    const [coverageSearch, setCoverageSearch] = useState('');
    const userRole = localStorage.getItem('role');
    const isSuperAdmin = userRole === ROLES.SUPER_ADMIN;

    const fetchTestList = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Authentication token not found.');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/testlist`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setTests(response.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch test list.');
            console.error('Error fetching test list:', err);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTestList();
    }, [fetchTestList]);

    const handleExportTests = () => {
        const filtered = tests.filter(test => {
            const tq = testQuery.trim().toLowerCase();
            return !tq || (test.TestName || '').toLowerCase().includes(tq);
        });
        if (!filtered.length) {
            alert('No data available to export.');
            return;
        }
        const data = filtered.map((test, i) => ({
            'Sl. No.': i + 1,
            'Test Name': test.TestName || '',
            'TAT (Days)': test.tat ?? '',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        ws['!cols'] = [{ wch: 8 }, { wch: 36 }, { wch: 12 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Test List');
        XLSX.writeFile(wb, 'Test_List.xlsx');
    };

    const openAddModal = () => {
        setEditData(null);
        setShowModal(true);
    };

    const openEditModal = (test) => {
        setEditData(test);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditData(null);
    };

    // Fetch AIMS charges + price list so we can flag tests that are missing either one
    const fetchPricingCoverage = useCallback(async () => {
        setCoverageLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const [aimsRes, priceRes] = await Promise.all([
                axios.get(`${API}/api/testlist/aimscharges`, { headers }),
                axios.get(`${API}/api/testlist/pricelist/all`, { headers }),
            ]);
            setAimsCharges(Array.isArray(aimsRes.data) ? aimsRes.data : []);
            setPriceListEntries(Array.isArray(priceRes.data) ? priceRes.data : []);
        } catch (err) {
            console.error('Error fetching pricing coverage:', err);
            toast.error('Failed to fetch AIMS charges / price list data.');
        } finally {
            setCoverageLoading(false);
        }
    }, []);

    // Cross-reference every test against AIMS charges + price list entries
    const coverageRows = useMemo(() => {
        return tests.map(test => {
            const name = normalizeName(test.TestName);
            const hasAims = aimsCharges.some(a => normalizeName(a.test) === name);
            const userTypesCovered = [...new Set(
                priceListEntries.filter(p => normalizeName(p.test_name) === name).map(p => p.user_type)
            )];
            return { ...test, hasAims, hasPriceList: userTypesCovered.length > 0, userTypesCovered };
        });
    }, [tests, aimsCharges, priceListEntries]);

    const missingAimsCount = coverageRows.filter(r => !r.hasAims).length;
    const missingPriceListCount = coverageRows.filter(r => !r.hasPriceList).length;
    const missingBothCount = coverageRows.filter(r => !r.hasAims && !r.hasPriceList).length;

    const filteredCoverageRows = useMemo(() => {
        let rows = coverageRows;
        if (coverageFilter === 'missingAny') rows = rows.filter(r => !r.hasAims || !r.hasPriceList);
        else if (coverageFilter === 'missingAims') rows = rows.filter(r => !r.hasAims);
        else if (coverageFilter === 'missingPriceList') rows = rows.filter(r => !r.hasPriceList);
        else if (coverageFilter === 'missingBoth') rows = rows.filter(r => !r.hasAims && !r.hasPriceList);

        if (coverageSearch.trim()) {
            const q = coverageSearch.trim().toLowerCase();
            rows = rows.filter(r => (r.TestName || '').toLowerCase().includes(q));
        }
        return rows;
    }, [coverageRows, coverageFilter, coverageSearch]);

    const openCoverageModal = async () => {
        setShowCoverageModal(true);
        await fetchPricingCoverage();
    };

    // Bulk import tests from validated Excel rows. New rows are created; rows the
    // user chose to "Replace" (duplicate test names) update the existing test's TAT.
    const handleTestsBulkImport = async (rows) => {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        let createdCount = 0;
        let updatedCount = 0;
        const failed = [];

        for (const row of rows) {
            const testName = String(row['Test Name'] ?? '').trim();
            const tatRaw = row['TAT (Days)'];
            const tat = tatRaw !== '' && tatRaw !== undefined && tatRaw !== null ? Number(tatRaw) : null;
            try {
                if (row._replaceId) {
                    await axios.put(
                        `${process.env.REACT_APP_API_BASE_URL}/api/testlist/${row._replaceId}`,
                        { TestName: testName, tat },
                        { headers }
                    );
                    updatedCount++;
                } else {
                    await axios.post(
                        `${process.env.REACT_APP_API_BASE_URL}/api/testlist`,
                        { TestName: testName, tat },
                        { headers }
                    );
                    createdCount++;
                }
            } catch (err) {
                failed.push(`${testName} (${err.response?.data?.error || err.response?.status || 'failed'})`);
            }
        }

        if (createdCount > 0) toast.success(`${createdCount} test(s) imported successfully.`);
        if (updatedCount > 0) toast.success(`${updatedCount} test(s) replaced successfully.`);
        if (failed.length > 0) {
            toast.error(`${failed.length} row(s) failed: ${failed.slice(0, 3).join('; ')}${failed.length > 3 ? '…' : ''}`);
        }
        if (createdCount > 0 || updatedCount > 0) await fetchTestList();
        setShowBulkUpload(false);

        // Immediately surface which of the newly imported tests still need AIMS/price list entries
        if (createdCount > 0 || updatedCount > 0) {
            await fetchPricingCoverage();
            setCoverageFilter('missingAny');
            setShowCoverageModal(true);
        }
    };

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Test List</h2>
            </div>

            {/* ─── Toolbar: search (left) · actions (right) ─── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '1 1 auto' }}>
                    <input
                        type="text"
                        placeholder="Search by test name"
                        value={testQuery}
                        onChange={e => setTestQuery(e.target.value)}
                        style={{ width: '100%', maxWidth: 320, padding: '9px 12px', fontSize: 14, border: '1.5px solid #d0d7de', borderRadius: 8 }}
                    />
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                        className={styles.exportIconBtn}
                        title="Export to Excel"
                        aria-label="Export to Excel"
                        onClick={handleExportTests}
                    >
                        <FaFileExcel size={20} />
                    </button>
                    {isSuperAdmin && (
                        <button className={styles.addBtn} onClick={openAddModal}>
                            + Add Test
                        </button>
                    )}
                    {isSuperAdmin && (
                        <button className={styles.addBtn} onClick={() => setShowBulkUpload(true)}>
                            ⬆ Bulk Upload
                        </button>
                    )}
                    <button className={styles.addBtn} onClick={openCoverageModal}>
                        📊 Check Pricing Coverage
                    </button>
                </div>
            </div>
            <AddTestForm
                show={showModal}
                onClose={closeModal}
                onSuccess={fetchTestList}
                editData={editData}
            />
            {showBulkUpload && (
                <BulkUploadModal
                    type="tests"
                    existingTests={tests}
                    onImport={handleTestsBulkImport}
                    onClose={() => setShowBulkUpload(false)}
                />
            )}
            {showCoverageModal && (
                <CoverageModal
                    loading={coverageLoading}
                    rows={filteredCoverageRows}
                    totalTests={tests.length}
                    missingAimsCount={missingAimsCount}
                    missingPriceListCount={missingPriceListCount}
                    missingBothCount={missingBothCount}
                    filter={coverageFilter}
                    onFilterChange={setCoverageFilter}
                    search={coverageSearch}
                    onSearchChange={setCoverageSearch}
                    onRefresh={fetchPricingCoverage}
                    onClose={() => setShowCoverageModal(false)}
                />
            )}
            <ToastContainer position="top-right" autoClose={3000} />
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Test Name</th>
                        <th>TAT (Days)</th>
                        {isSuperAdmin && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {tests
                        .filter(test => {
                            const tq = testQuery.trim().toLowerCase();
                            return !tq || (test.TestName || '').toLowerCase().includes(tq);
                        })
                        .map(test => (
                        <tr key={test.id}>
                            <td>{test.id}</td>
                            <td>{test.TestName}</td>
                            <td>{test.tat}</td>
                            {isSuperAdmin && (
                                <td>
                                    <button
                                        className={styles.editBtn}
                                        onClick={() => openEditModal(test)}
                                        title="Edit test"
                                    >
                                        ✏️
                                    </button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ── Pricing coverage preview modal ─────────────────────────────────────────────

const CoverageModal = ({
    loading, rows, totalTests, missingAimsCount, missingPriceListCount, missingBothCount,
    filter, onFilterChange, search, onSearchChange, onRefresh, onClose,
}) => {
    const tabs = [
        { key: 'missingAny', label: 'Missing Either' },
        { key: 'missingAims', label: 'Missing AIMS Charge' },
        { key: 'missingPriceList', label: 'Missing Price List' },
        { key: 'missingBoth', label: 'Missing Both' },
        { key: 'all', label: 'All Tests' },
    ];

    return (
        <div style={CS.overlay} onClick={onClose}>
            <div style={CS.modal} onClick={e => e.stopPropagation()}>
                <div style={CS.header}>
                    <h3 style={CS.title}>Pricing Coverage</h3>
                    <button style={CS.closeBtn} onClick={onClose}>&times;</button>
                </div>

                <div style={CS.body}>
                    <div style={CS.summaryBar}>
                        <span style={{ ...CS.badge, background: '#e5e7eb', color: '#374151' }}>Total tests: {totalTests}</span>
                        <span style={{ ...CS.badge, background: '#fee2e2', color: '#991b1b' }}>Missing AIMS: {missingAimsCount}</span>
                        <span style={{ ...CS.badge, background: '#fef3c7', color: '#92400e' }}>Missing Price List: {missingPriceListCount}</span>
                        <span style={{ ...CS.badge, background: '#fecaca', color: '#7f1d1d' }}>Missing Both: {missingBothCount}</span>
                        <button style={CS.refreshBtn} onClick={onRefresh} disabled={loading}>
                            {loading ? 'Refreshing...' : '↻ Refresh'}
                        </button>
                    </div>

                    <div style={CS.tabsBar}>
                        {tabs.map(t => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => onFilterChange(t.key)}
                                style={{ ...CS.tabBtn, ...(filter === t.key ? CS.tabBtnActive : {}) }}
                            >
                                {t.label}
                            </button>
                        ))}
                        <input
                            type="text"
                            placeholder="Search test name"
                            value={search}
                            onChange={e => onSearchChange(e.target.value)}
                            style={CS.searchInput}
                        />
                    </div>

                    {loading ? (
                        <div style={{ padding: '30px 0', textAlign: 'center', color: '#6b7280' }}>Loading coverage data...</div>
                    ) : rows.length === 0 ? (
                        <div style={{ padding: '30px 0', textAlign: 'center', color: '#6b7280' }}>
                            No tests match this filter — nice, coverage looks good here.
                        </div>
                    ) : (
                        <div style={CS.tableWrap}>
                            <table style={CS.table}>
                                <thead>
                                    <tr>
                                        <th style={CS.th}>Test Name</th>
                                        <th style={CS.th}>TAT (Days)</th>
                                        <th style={{ ...CS.th, textAlign: 'center' }}>AIMS Charge</th>
                                        <th style={{ ...CS.th, textAlign: 'center' }}>Price List</th>
                                        <th style={CS.th}>User Types Covered</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => (
                                        <tr key={r.id}>
                                            <td style={CS.td}>{r.TestName}</td>
                                            <td style={CS.td}>{r.tat ?? '—'}</td>
                                            <td style={{ ...CS.td, textAlign: 'center' }}>
                                                {r.hasAims
                                                    ? <span style={CS.okBadge}>✓ Yes</span>
                                                    : <span style={CS.missingBadge}>✗ Missing</span>}
                                            </td>
                                            <td style={{ ...CS.td, textAlign: 'center' }}>
                                                {r.hasPriceList
                                                    ? <span style={CS.okBadge}>✓ Yes</span>
                                                    : <span style={CS.missingBadge}>✗ Missing</span>}
                                            </td>
                                            <td style={{ ...CS.td, color: '#6b7280', fontSize: 12 }}>
                                                {r.userTypesCovered.length ? r.userTypesCovered.join(', ') : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const CS = {
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.52)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
    },
    modal: {
        background: '#fff', borderRadius: 10,
        width: '95vw', maxWidth: 1000, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
    },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 24px', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
    },
    title: { margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' },
    closeBtn: {
        background: 'none', border: 'none', fontSize: 26,
        cursor: 'pointer', color: '#6b7280', lineHeight: 1, padding: 0,
    },
    body: { padding: 24, overflowY: 'auto', flex: 1 },
    summaryBar: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 },
    badge: { padding: '4px 14px', borderRadius: 20, fontWeight: 600, fontSize: 13 },
    refreshBtn: {
        marginLeft: 'auto', padding: '6px 14px', background: '#fff', color: '#374151',
        border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12,
    },
    tabsBar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 },
    tabBtn: {
        padding: '6px 14px', background: '#f3f4f6', color: '#374151',
        border: '1px solid #d1d5db', borderRadius: 20, cursor: 'pointer', fontWeight: 600, fontSize: 12,
    },
    tabBtnActive: { background: '#1d6fa4', color: '#fff', borderColor: '#1d6fa4' },
    searchInput: {
        marginLeft: 'auto', padding: '7px 12px', fontSize: 13,
        border: '1.5px solid #d0d7de', borderRadius: 8, minWidth: 200,
    },
    tableWrap: {
        overflowX: 'auto', overflowY: 'auto', maxHeight: '50vh',
        border: '1px solid #e5e7eb', borderRadius: 8,
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: {
        padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12,
        color: '#374151', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap',
        position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1,
    },
    td: { padding: '8px 12px', borderBottom: '1px solid #e5e7eb' },
    okBadge: {
        background: '#dcfce7', color: '#166534',
        padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    },
    missingBadge: {
        background: '#fee2e2', color: '#991b1b',
        padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    },
};

export default TestList;
